import * as React from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { useSnapshot, ref } from 'valtio'
import { dayHistoryState, resetDayHistory } from '../../../../store/dayHistoryState.ts'
import { authState, effectiveTimezone } from '../../../../store/authState.ts'
import { historyState } from '../../../../store/historyState.ts'
import { dataState } from '../../../../store/dataState.ts'
import { deviceFromPet } from '../../../../helpers/dataHelpers.ts'
import { fetchDayHistory } from '../../../../helpers/dayHistoryHelper.ts'
import { DayHistoryControls } from './DayHistoryControls.tsx'
import { DayHistoryTimeline } from './DayHistoryTimeline.tsx'

/**
 * Day History Panel — replaces the location list when day history mode is active.
 * Contains: week strip date selector, timeline scrubber, playback controls.
 */
export const DayHistoryPanel: React.FC = () => {
    const selectedDate = (useSnapshot(dayHistoryState) as typeof dayHistoryState).selectedDate
    const loading = (useSnapshot(dayHistoryState) as typeof dayHistoryState).loading
    useSnapshot(authState)
    const zone = effectiveTimezone().value

    // Current week anchor — the Monday of the week containing selectedDate
    const weekStart = React.useMemo(() => {
        if (!selectedDate) return DateTime.now().setZone(zone).startOf('week')
        return selectedDate.startOf('week')
    }, [selectedDate, zone])

    // Generate 7 days for the week strip
    const weekDays = React.useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => weekStart.plus({ days: i }))
    }, [weekStart])

    // Month/year label — if week spans two months, show both
    const monthLabel = React.useMemo(() => {
        const first = weekDays[0]
        const last = weekDays[6]
        if (first.month === last.month) {
            return first.toFormat('MMMM yyyy')
        }
        return `${first.toFormat('MMM')} – ${last.toFormat('MMM yyyy')}`
    }, [weekDays])

    const today = DateTime.now().setZone(zone).startOf('day')

    const shiftWeek = (direction: -1 | 1) => {
        const newDate = (selectedDate || today).plus({ weeks: direction })
        void loadDay(newDate)
    }

    const selectDay = (day: DateTime) => {
        // Don't allow future dates
        if (day > today) return
        void loadDay(day)
    }

    const loadDay = async (day: DateTime) => {
        const store = dayHistoryState
        const selectedPetId = historyState.selectedPetId
        if (!selectedPetId) return

        const pets = dataState.pets
        const pet = pets.find(p => p._id === selectedPetId)
        if (!pet) return

        const device = deviceFromPet(pet)
        if (!device) return

        store.isPlaying = false
        store.loading = true
        store.selectedDate = ref(day) as unknown as DateTime
        store.pathData = null
        store.error = ''

        const data = await fetchDayHistory(device.iccid, pet._id, day)

        if (data) {
            store.pathData = data
        } else {
            store.error = 'Failed to load day history'
        }
        store.loading = false
    }

    const isSelected = (day: DateTime) => {
        if (!selectedDate) return false
        return day.hasSame(selectedDate, 'day')
    }

    const isFuture = (day: DateTime) => day > today

    return (
        <div className={'flex flex-col gap-1 px-2 py-1 w-full'}>
            {/* Header with exit button */}
            <div className={'flex items-center justify-between'}>
                <span className={'text-sm font-semibold text-primary'}>Day History</span>
                <button
                    className={'btn btn-sm btn-secondary'}
                    onClick={() => resetDayHistory()}
                >
                    Exit
                </button>
            </div>

            {/* Week strip */}
            <div className={'flex items-center gap-1'}>
                <button
                    className={'btn btn-ghost btn-xs btn-circle text-primary'}
                    onClick={() => shiftWeek(-1)}
                    disabled={loading}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className={'flex-1 grid grid-cols-7 gap-0.5'}>
                    {weekDays.map(day => {
                        const selected = isSelected(day)
                        const future = isFuture(day)
                        return (
                            <div
                                key={day.toISODate()}
                                className={`flex flex-col items-center py-1 rounded-lg
                                ${selected ? 'bg-primary text-primary-content' : 'bg-transparent text-primary'}
                                    ${future ? 'opacity-30' : 'cursor-pointer active:opacity-70'}
                                `}
                                onClick={() => !future && !loading && selectDay(day)}
                            >
                                <span className={'text-xs font-medium'}>{day.toFormat('ccc')}</span>
                                <span className={'text-sm font-bold'}>{day.day}</span>
                            </div>
                        )
                    })}
                </div>

                <button
                    className={'btn btn-ghost btn-xs btn-circle text-primary'}
                    onClick={() => shiftWeek(1)}
                    disabled={loading || weekDays[6] >= today}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>

            {/* Month label */}
            <div className={'text-center text-xs text-gray-500 font-medium -mt-1'}>
                {monthLabel}
            </div>

            {/* Timeline scrubber */}
            <DayHistoryTimeline />

            {/* Playback controls */}
            <DayHistoryControls />
        </div>
    )
}
